import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  page,
  pageAndAlunoCodigo,
  pageAndAlunoCodigoAndDataAteAndDataDe,
  pageAndAlunoCodigoAndDataParaAteAndDataParaDe,
  save,
} from "./api-comunicado-usuario";
import ComunicadoUsuarioFindDialog from "./ComunicadoUsuarioFindDialog";
import ComunicadoUsuarioForm from "./ComunicadoUsuarioForm";
import ComunicadoUsuarioList from "./ComunicadoUsuarioList";
import { RESPONSAVEL } from "../../Usuario/Perfil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import TextFieldDate from "../../UI/TextFieldDate";

const TODOS = "todos";
const POR_ALUNO = "porAluno";
const POR_DATA_ENVIO = "porAlunoEDataHoraEnvio";
const POR_DATA_PARA = "porAlunoEDataHoraPara";

const FILTROS = [
  { value: TODOS, label: "Todos" },
  { value: POR_ALUNO, label: "Por Aluno" },
  { value: POR_DATA_ENVIO, label: "Por Aluno e Data Hora Envio" },
  { value: POR_DATA_PARA, label: "Por Aluno e Data Hora Para" },
];

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const isDateFiltro = (filtro) => filtro === POR_DATA_ENVIO || filtro === POR_DATA_PARA;

const requestPage = ({ filtro, de, ate, pageNumber }) => {
  if (filtro === POR_ALUNO) return pageAndAlunoCodigo(pageNumber);
  if (filtro === POR_DATA_ENVIO) return pageAndAlunoCodigoAndDataAteAndDataDe(pageNumber, ate, de);
  if (filtro === POR_DATA_PARA) return pageAndAlunoCodigoAndDataParaAteAndDataParaDe(pageNumber, ate, de);
  return page(pageNumber);
};

export default function ComunicadoUsuarioContainer() {
  const user = useSelector((state) => state.usuario);
  const [comunicadoUsuario, setComunicadoUsuario] = useState(null);
  const [listCU, setListCU] = useState(EMPTY_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [filtro, setFiltro] = useState(TODOS);
  const [datas, setDatas] = useState({
    [POR_DATA_ENVIO]: { de: dayjs().subtract(20, "day"), ate: dayjs() },
    [POR_DATA_PARA]: { de: dayjs().subtract(5, "day"), ate: dayjs().add(5, "day") },
  });
  // Filtro efetivamente aplicado na busca (as datas só valem após "Aplicar").
  const [query, setQuery] = useState({ filtro: TODOS, de: null, ate: null, pageNumber: 0 });

  const alunoSelecionado = user.arAlunoSelecionado;

  useEffect(() => {
    let active = true;
    // Sem aluno selecionado, os filtros por aluno não se aplicam.
    const effectiveQuery = alunoSelecionado ? query : { ...query, filtro: TODOS };
    setIsLoading(true);
    requestPage(effectiveQuery)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setListCU(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query, alunoSelecionado]);

  const applyFiltro = (novoFiltro, novasDatas = datas) => {
    if (!isDateFiltro(novoFiltro)) {
      setQuery({ filtro: novoFiltro, de: null, ate: null, pageNumber: 0 });
      return;
    }
    const { de, ate } = novasDatas[novoFiltro];
    if (!de || !de.isValid()) {
      errorToast("Data De não preenchida.");
      return;
    }
    if (!ate || !ate.isValid()) {
      errorToast("Data Até não preenchida.");
      return;
    }
    if (de.isAfter(ate, "day")) {
      errorToast("Data De não pode ser maior que a Data Até.");
      return;
    }
    setQuery({
      filtro: novoFiltro,
      de: de.startOf("day").toDate(),
      ate: ate.endOf("day").toDate(),
      pageNumber: 0,
    });
  };

  const handleChangeFiltro = (event) => {
    setFiltro(event.target.value);
    applyFiltro(event.target.value);
  };

  const handleDataChange = (campo) => (value) => {
    setDatas((prevState) => ({
      ...prevState,
      [filtro]: { ...prevState[filtro], [campo]: value ? dayjs(value) : null },
    }));
  };

  const handleChangePage = (event, newPage) => {
    setQuery((prevState) => ({ ...prevState, pageNumber: newPage }));
  };

  const updateInList = (data) => {
    setListCU((prevState) => ({
      ...prevState,
      content: prevState.content.map((cu) => (cu.codigo === data.codigo ? data : cu)),
    }));
  };

  const handleOpen = (cu) => {
    if (cu.visualizado) {
      setComunicadoUsuario(cu);
      return;
    }
    const persistent = { ...cu, visualizado: true };
    setComunicadoUsuario(persistent);
    save(persistent).then((data) => {
      if (!data || data.error) return;
      updateInList(data);
      setComunicadoUsuario((prevState) => (prevState?.codigo === data.codigo ? data : prevState));
    });
  };

  const handleConfirmRead = () => {
    setIsConfirming(true);
    save({ ...comunicadoUsuario, visualizado: true, leituraConfirmada: true })
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        updateInList(data);
        setComunicadoUsuario(data);
        success("Leitura confirmada.");
      })
      .finally(() => setIsConfirming(false));
  };

  const renderFiltros = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="filtro-label">Filtro</InputLabel>
          <Select labelId="filtro-label" label="Filtro" value={filtro} onChange={handleChangeFiltro}>
            {FILTROS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isDateFiltro(filtro) && (
          <>
            <TextFieldDate
              label="De"
              value={datas[filtro].de}
              onChange={handleDataChange("de")}
              slotProps={{ textField: { size: "small" } }}
            />
            <TextFieldDate
              label="Até"
              value={datas[filtro].ate}
              onChange={handleDataChange("ate")}
              slotProps={{ textField: { size: "small" } }}
            />
            <Button variant="contained" onClick={() => applyFiltro(filtro)}>
              Aplicar
            </Button>
          </>
        )}
      </Stack>
      {filtro !== TODOS && !alunoSelecionado && (
        <Typography variant="caption" color="text.secondary" component="div" mt={1}>
          Nenhum aluno selecionado: mostrando todos os comunicados.
        </Typography>
      )}
    </Paper>
  );

  return (
    <Container>
      <ComunicadoUsuarioFindDialog
        openSearchComunicado={openSearch}
        setOpenSearchComunicado={setOpenSearch}
        setComunicadoUsuario={handleOpen}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        {comunicadoUsuario ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Voltar para a lista">
              <IconButton edge="start" onClick={() => setComunicadoUsuario(null)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6">Comunicado</Typography>
          </Stack>
        ) : (
          <Box>
            <Typography variant="h6">Comunicados</Typography>
            <Typography variant="body2" color="text.secondary">
              Comunicados recebidos da instituição.
            </Typography>
          </Box>
        )}
        {!comunicadoUsuario && (
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => setOpenSearch(true)}>
            Procurar
          </Button>
        )}
      </Stack>

      {comunicadoUsuario ? (
        <ComunicadoUsuarioForm
          comunicadoUsuario={comunicadoUsuario}
          handleConfirmRead={handleConfirmRead}
          isConfirming={isConfirming}
        />
      ) : (
        <>
          {user.perfil === RESPONSAVEL && renderFiltros()}
          <ComunicadoUsuarioList
            listCU={listCU}
            isLoading={isLoading}
            handleChangePage={handleChangePage}
            handleRowClick={handleOpen}
          />
        </>
      )}
    </Container>
  );
}
